import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { AgentCard, AgentBuilder, AgentRunHistory } from '../components/agents';
import { Lightning, Plus, SpinnerGap, Sparkle, X, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyAgent, setHistoryAgent] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState([]);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agents`, { withCredentials: true });
      setAgents(res.data.agents || []);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agents/templates`, { withCredentials: true });
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agents/suggestions`, { withCredentials: true });
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchTemplates();
    fetchSuggestions();
  }, [fetchAgents, fetchTemplates, fetchSuggestions]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (payload.agent_id) {
        await axios.put(`${API_URL}/api/agents/${payload.agent_id}`, payload, { withCredentials: true });
        toast.success('Routine updated');
      } else {
        await axios.post(`${API_URL}/api/agents`, payload, { withCredentials: true });
        toast.success('Routine created');
      }
      setShowBuilder(false);
      setEditingAgent(null);
      await fetchAgents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save routine');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async (agentId) => {
    try {
      toast.info('Routine started running…');
      await axios.post(`${API_URL}/api/agents/${agentId}/run`, {}, { withCredentials: true });
      // Poll for completion after a short delay
      setTimeout(() => fetchAgents(), 3000);
      setTimeout(() => fetchAgents(), 8000);
    } catch (err) {
      toast.error('Failed to run routine');
    }
  };

  const handleToggle = async (agentId, enabled) => {
    try {
      await axios.put(`${API_URL}/api/agents/${agentId}`, { enabled }, { withCredentials: true });
      setAgents(prev => prev.map(a => a.agent_id === agentId ? { ...a, enabled } : a));
      toast.success(enabled ? 'Routine enabled' : 'Routine paused');
    } catch (err) {
      toast.error('Failed to toggle routine');
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Delete this routine? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/agents/${agentId}`, { withCredentials: true });
      setAgents(prev => prev.filter(a => a.agent_id !== agentId));
      toast.success('Routine deleted');
    } catch (err) {
      toast.error('Failed to delete routine');
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setShowBuilder(true);
  };

  const handleViewHistory = async (agent) => {
    setHistoryAgent(agent);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/agents/${agent.agent_id}/history`, { withCredentials: true });
      setHistoryData(res.data.history || []);
    } catch (err) {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-10" data-testid="agents-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="display-lg text-4xl">Routines</h1>
            <p className="body-lg mt-3 max-w-xl">
              Smart routines that run on your schedule. Check bills, summarize chats, send updates — hands-free.
            </p>
          </div>
          <button
            onClick={() => { setEditingAgent(null); setShowBuilder(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0f172a] text-white text-sm font-semibold hover:bg-[#1e293b] transition-colors flex-shrink-0"
            data-testid="create-agent-btn"
          >
            <Plus size={16} weight="bold" /> New Routine
          </button>
        </div>

        {/* Suggestions Banner */}
        <SuggestionsBanner
          suggestions={suggestions.filter(s => !dismissedSuggestions.includes(s.id))}
          onDismiss={(id) => setDismissedSuggestions(prev => [...prev, id])}
          onUseTemplate={(templateId) => {
            setEditingAgent(null);
            setShowBuilder(true);
            // Small delay so builder opens first, then we can trigger template selection
            setTimeout(() => {
              const btn = document.querySelector(`[data-testid="template-${templateId}"]`);
              if (btn) btn.click();
            }, 300);
          }}
        />

        {/* Agent Cards Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <SpinnerGap size={28} className="text-[#005bc1] animate-spin" />
          </div>
        ) : agents.length === 0 ? (
          <EmptyState onCreate={() => setShowBuilder(true)} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agents-grid">
            {agents.map(agent => (
              <AgentCard
                key={agent.agent_id}
                agent={agent}
                onRun={handleRun}
                onToggle={handleToggle}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onViewHistory={() => handleViewHistory(agent)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Agent Builder Modal */}
      {showBuilder && (
        <AgentBuilder
          templates={templates}
          editingAgent={editingAgent}
          onSave={handleSave}
          onClose={() => { setShowBuilder(false); setEditingAgent(null); }}
          saving={saving}
        />
      )}

      {/* Run History Modal */}
      {historyAgent && !historyLoading && (
        <AgentRunHistory
          agentName={historyAgent.name}
          history={historyData}
          onClose={() => { setHistoryAgent(null); setHistoryData([]); }}
        />
      )}
    </Layout>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="surface-card p-12 text-center max-w-md mx-auto" data-testid="agents-empty-state">
      <div className="w-16 h-16 rounded-2xl bg-[#0f172a] flex items-center justify-center mx-auto mb-5">
        <Lightning size={28} className="text-[#f59e0b]" weight="fill" />
      </div>
      <h3 className="font-bold text-lg text-[#2d3338] mb-2">No routines yet</h3>
      <p className="text-sm text-[#596065] mb-6">
        Create your first routine to automate recurring tasks like morning briefings, bill reminders, or team updates.
      </p>
      <button
        onClick={onCreate}
        className="btn-primary text-sm"
        data-testid="empty-create-agent-btn"
      >
        <Plus size={16} weight="bold" className="inline mr-1" /> Create Your First Routine
      </button>
    </div>
  );
}


function SuggestionsBanner({ suggestions, onDismiss, onUseTemplate }) {
  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="mb-6" data-testid="suggestions-banner">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={14} className="text-[#f59e0b]" weight="fill" />
        <span className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Suggested for you</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {suggestions.map((s) => (
          <div
            key={s.id}
            className="relative flex-shrink-0 w-72 p-4 rounded-xl bg-gradient-to-br from-[#fefce8] to-white border border-[#fef08a]/50 hover:border-[#f59e0b]/30 transition-all group"
            data-testid={`suggestion-${s.id}`}
          >
            <button
              onClick={() => onDismiss(s.id)}
              className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-[#fef3c7] text-[#acb3b8] hover:text-[#596065] transition-all"
              data-testid={`dismiss-suggestion-${s.id}`}
            >
              <X size={12} />
            </button>
            <p className="font-bold text-sm text-[#2d3338] mb-1">{s.title}</p>
            <p className="text-[11px] text-[#78716c] mb-2 leading-relaxed">{s.reason}</p>
            <p className="text-[10px] text-[#596065] mb-3">{s.description}</p>
            <button
              onClick={() => onUseTemplate(s.template_id)}
              className="flex items-center gap-1 text-[11px] font-semibold text-[#b45309] hover:text-[#92400e] transition-colors"
              data-testid={`use-suggestion-${s.id}`}
            >
              Set this up <ArrowRight size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
